/**
 * 飞书 JSAPI 工具类
 * 文档：https://open.feishu.cn/document/uYjL24iN/uYjN3QjL2YzN04iN2cDN
 */

// 声明飞书全局对象
declare global {
  interface Window {
    h5sdk?: {
      config: (config: FeishuConfig) => void;
      ready: (callback: () => void) => void;
      error: (callback: (error: any) => void) => void;
      setNavigationBarTitle: (params: { title: string }) => void;
      setNavigationBarColor: (params: {
        frontColor: string;
        backgroundColor: string;
      }) => void;
      showNavigationBarLoading: () => void;
      hideNavigationBarLoading: () => void;
      // 可以继续扩展其他 API
    };
  }
}

interface FeishuConfig {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  onSuccess?: (res: any) => void;
  onFail?: (err: any) => void;
}

// ========== OAuth 登录相关 ==========

/**
 * 生成随机状态码用于CSRF防护
 */
function generateState(): string {
  const randomStr = Math.random().toString(36).substring(2, 15) +
                    Math.random().toString(36).substring(2, 15);
  const timestamp = Date.now().toString();
  return `${randomStr}_${timestamp}`;
}

/**
 * 验证状态码（简单的时间戳验证，防止重放攻击）
 */
export function verifyState(state: string): boolean {
  try {
    const parts = state.split('_');
    if (parts.length !== 2) {
      return false;
    }
    const timestamp = parseInt(parts[1], 10);
    const now = Date.now();
    // 状态码有效期：5分钟
    return now - timestamp < 5 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * 获取飞书OAuth授权URL
 */
export function getFeishuAuthorizeUrl(): string {
  const state = generateState();
  sessionStorage.setItem('feishu_oauth_state', state);

  // 使用与后端一致的 App ID
  const appId = 'cli_a94f9af491389bd3';
  const redirectUri = `${window.location.origin}/login/feishu/callback`;

  return `https://open.feishu.cn/open-apis/authen/v1/authorize?app_id=${appId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
}

// ========== JSAPI SDK 相关 ==========

class FeishuSDK {
  private isReady: boolean = false;
  private readyCallbacks: Array<() => void> = [];
  private isFeishuEnv: boolean = false;

  constructor() {
    this.detectFeishuEnv();
  }

  /**
   * 检测是否在飞书环境中
   */
  private detectFeishuEnv() {
    const ua = navigator.userAgent.toLowerCase();
    this.isFeishuEnv = ua.includes('lark') || ua.includes('feishu');
  }

  /**
   * 是否在飞书环境中
   */
  isInFeishu(): boolean {
    return this.isFeishuEnv;
  }

  /**
   * 加载飞书 JSAPI SDK
   */
  loadSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isFeishuEnv) {
        console.log('Not in Feishu environment');
        resolve();
        return;
      }

      if (window.h5sdk) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      // 1.5.20+ 是启用 getLocation v2 新版定位 API 的前置条件（见 飞书定位适配指南）
      script.src = 'https://lf1-cdn-tos.bytegoofy.com/goofy/lark/op/h5-js-sdk-1.5.20.js';
      script.onload = () => {
        console.log('Feishu SDK loaded');
        resolve();
      };
      script.onerror = () => {
        console.error('Failed to load Feishu SDK');
        reject(new Error('Failed to load Feishu SDK'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * 配置飞书 JSAPI
   * 需要后端提供签名接口
   */
  async config(config: FeishuConfig): Promise<void> {
    if (!this.isFeishuEnv || !window.h5sdk) {
      console.log('Feishu SDK not available');
      return;
    }

    return new Promise((resolve, reject) => {
      window.h5sdk!.config({
        ...config,
        onSuccess: (res: any) => {
          console.log('Feishu config success', res);
          this.isReady = true;
          this.readyCallbacks.forEach(callback => callback());
          this.readyCallbacks = [];
          config.onSuccess?.(res);
          resolve();
        },
        onFail: (err: any) => {
          console.error('Feishu config failed', err);
          config.onFail?.(err);
          reject(err);
        },
      });
    });
  }

  /**
   * SDK 准备完成后执行回调
   */
  ready(callback: () => void) {
    if (!this.isFeishuEnv) {
      return;
    }

    if (this.isReady) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  /**
   * 设置页面标题
   */
  setTitle(title: string) {
    if (!this.isFeishuEnv || !window.h5sdk) {
      console.log('Not in Feishu, setting document title');
      document.title = title;
      return;
    }

    this.ready(() => {
      try {
        window.h5sdk!.setNavigationBarTitle({ title });
        console.log('Set Feishu title:', title);
      } catch (error) {
        console.error('Failed to set Feishu title:', error);
      }
    });
  }

  /**
   * 设置导航栏颜色
   */
  setNavigationBarColor(frontColor: string, backgroundColor: string) {
    if (!this.isFeishuEnv || !window.h5sdk) {
      return;
    }

    this.ready(() => {
      try {
        window.h5sdk!.setNavigationBarColor({
          frontColor,
          backgroundColor,
        });
      } catch (error) {
        console.error('Failed to set navigation bar color:', error);
      }
    });
  }

  /**
   * 显示导航栏加载状态
   */
  showNavigationBarLoading() {
    if (!this.isFeishuEnv || !window.h5sdk) {
      return;
    }

    this.ready(() => {
      try {
        window.h5sdk!.showNavigationBarLoading();
      } catch (error) {
        console.error('Failed to show navigation bar loading:', error);
      }
    });
  }

  /**
   * 隐藏导航栏加载状态
   */
  hideNavigationBarLoading() {
    if (!this.isFeishuEnv || !window.h5sdk) {
      return;
    }

    this.ready(() => {
      try {
        window.h5sdk!.hideNavigationBarLoading();
      } catch (error) {
        console.error('Failed to hide navigation bar loading:', error);
      }
    });
  }
}

// 导出单例
export const feishuSDK = new FeishuSDK();

/**
 * 初始化飞书 SDK
 * 应在应用启动时调用
 */
export async function initFeishuSDK() {
  try {
    await feishuSDK.loadSDK();

    // 如果需要鉴权，这里调用后端接口获取签名信息
    // const { appId, timestamp, nonceStr, signature } = await getFeishuSignature();
    // await feishuSDK.config({
    //   appId,
    //   timestamp,
    //   nonceStr,
    //   signature,
    //   jsApiList: ['setNavigationBarTitle', 'setNavigationBarColor'],
    // });

    // 暂时不配置，直接使用（某些 API 不需要鉴权）
    if (feishuSDK.isInFeishu()) {
      console.log('Running in Feishu environment');
    }
  } catch (error) {
    console.error('Failed to initialize Feishu SDK:', error);
  }
}

export default feishuSDK;
